import { memo, useCallback, useContext } from "react";
import PropTypes from "prop-types";
import Modal from "react-modal";
import "./ShopSwitcherDetailModal.scss";
import { FormattedMessage } from "react-intl";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import ShopSwitcherDetailContent, {
	TK_EXTERNAL_MARKET_REDIRECTS,
} from "app/pages/Home/ShopSwitcher/ShopSwitcherDetailContent";
import { Form, Formik } from "formik";
import { sendTagOnChangeLanguage } from "app/utils/analytics";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import AppGlobalsContext from "app/AppGlobalsContext";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import classNames from "classnames";
import { BRANDS } from "app/constants";
import RelativeLink from "app/pages/.shared/RelativeLink";
import LogoBrandContainer from "app/pages/.shared/LogoBrand/LogoBrandContainer";
import IconCircleClose from "app/pages/.shared/IconCircleClose";
import AsidePanel from "app/pages/.shared/AsidePanel/AsidePanel";
import { redirectToShopUrl } from "app/utils/utils";
import { useLocation } from "react-router-dom";
import { CrossIcon } from "app/icons/navigation";

const ShopSwitcherDetailModal = ({
	isOpened,
	close,
	shops = [],
	currentShop = "",
	currentCurrency = "",
	updateCurrency = () => {},
	hideSwitcherModal = () => {},
}) => {
	const { resolution, envVars, brand } = useContext(AppGlobalsContext);
	const { track } = useAnalytics();
	const isBrandSV = brand === BRANDS.SV;
	const { pathname, search } = useLocation();

	const proxyBasenamePrefix = envVars?.PROXY_BASENAME_PREFIX;

	const isStopover = proxyBasenamePrefix?.includes("stopover");

	const isMobile = resolution === RESOLUTION.MEDIUM || resolution === RESOLUTION.SMALL;

	const filteredShops = isBrandSV
		? shops.filter(shop => {
				const code = shop?.code || "";
				const label = shop?.label || "";
				const isSaudiShop =
					isStopover && (code.includes("-SA") || label === "المملكة العربية السعودية");

				return !isSaudiShop;
			})
		: shops;

	// Stop propagation of dropdown events (click, touch, blur) to prevent the parent component from mistakenly closing the modal
	const stopPropagation = useCallback(e => {
		if (e) {
			e.stopPropagation();
		}
	}, []);

	const handleSubmit = useCallback(
		(values, { setSubmitting }) => {
			const [currentLanguageCode, currentCountryCode] = currentShop.split("-");
			const selectedCurrency = values?.currency;
			const selectedCurrencyCode = selectedCurrency?.code;
			const selectedLanguageCode = values?.language?.code;
			const selectedCountryCode = values?.country?.code;

			setSubmitting(false);
			hideSwitcherModal();

			const languageChanged = selectedLanguageCode !== currentLanguageCode;
			const countryChanged = selectedCountryCode !== currentCountryCode;
			const currencyChanged = currentCurrency !== selectedCurrencyCode;

			if (countryChanged) {
				const englishCountryName =
					new Intl.DisplayNames(["en"], { type: "region" }).of(selectedCountryCode) ||
					selectedCountryCode;

				track("country_selector_selected", {
					country: englishCountryName,
					source_site: window.location.hostname,
					device: isMobile ? "mobile" : "desktop",
				});
			}

			const externalMarket =
				brand === BRANDS.TK &&
				TK_EXTERNAL_MARKET_REDIRECTS.find(
					market => market.countryCode === selectedCountryCode
				);

			if (externalMarket) {
				window.open(externalMarket.url, "_blank", "noopener,noreferrer");
				return;
			}

			if (currencyChanged) {
				updateCurrency(selectedCurrency?.code);
			}

			if (countryChanged) {
				const isListingPage =
					pathname.includes("sdp/listing") ||
					pathname.includes("stopover/listing") ||
					pathname.includes("hotelonly/listing");

				redirectToShopUrl({
					language: selectedLanguageCode,
					country: selectedCountryCode,
					proxyBasenamePrefix,
					pathname: isListingPage ? "/" : pathname,
					search,
				});
			} else if (languageChanged) {
				sendTagOnChangeLanguage(selectedLanguageCode);

				const url = new URL(window.location.href);
				url.pathname = url.pathname.replace(
					`/${currentLanguageCode}`,
					`/${selectedLanguageCode}`
				);
				window.location.href = url.href;
			}
		},
		[currentShop, currentCurrency, pathname, search, isMobile, brand, track]
	);

	const modalContent = (
		<div
			className="shop-switcher-detail-modal__content"
			onClick={isMobile ? stopPropagation : undefined}
		>
			{!isMobile && (
				<div className="shop-switcher-detail-modal__close" onClick={close}>
					<CrossIcon />
				</div>
			)}
			<Typography
				className="shop-switcher-detail-modal__title"
				isBold
				variant={TYPOGRAPHY_VARIANTS.XL}
			>
				<FormattedMessage id="general.shop.switcher.detail.title" />
			</Typography>
			<Formik
				initialValues={{
					country: { code: "", label: "" },
					language: { code: "", label: "" },
					currency: { value: "", symbol: "" },
				}}
				validateOnChange={false}
				validateOnBlur={false}
				onSubmit={handleSubmit}
				initialTouched={{
					country: true,
					language: true,
					currency: true,
				}}
			>
				{() => (
					<Form onClick={isMobile ? stopPropagation : undefined}>
						<ShopSwitcherDetailContent
							shops={filteredShops}
							currentShop={currentShop}
							currentCurrency={currentCurrency}
						/>
					</Form>
				)}
			</Formik>
		</div>
	);

	return isMobile ? (
		<AsidePanel
			isOpen={isOpened}
			className={classNames("shop-switcher-detail-modal__side-panel ", {
				"shop-switcher-detail-modal--eh": brand === BRANDS.EK,
			})}
			data-testid="shop-switcher-detail-modal-side-panel"
			onClick={stopPropagation}
		>
			<div
				className="shop-switcher-detail-modal__side-panel-header"
				onClick={stopPropagation}
			>
				<RelativeLink to={{ pathname: "/" }} onClick={close}>
					<LogoBrandContainer />
				</RelativeLink>
				<IconCircleClose width={25} height={25} onClick={close} />
			</div>
			{modalContent}
		</AsidePanel>
	) : (
		<Modal
			isOpen={isOpened}
			onRequestClose={close}
			className="shop-switcher-detail-modal"
			overlayClassName="shop-switcher-detail-modal__overlay"
		>
			{modalContent}
		</Modal>
	);
};

ShopSwitcherDetailModal.propTypes = {
	isOpened: PropTypes.bool,
	close: PropTypes.func,
	shops: PropTypes.array,
	currentShop: PropTypes.string,
	updateCurrency: PropTypes.func,
	currentCurrency: PropTypes.string,
	hideSwitcherModal: PropTypes.func,
};

export default memo(ShopSwitcherDetailModal);
